import moment from 'moment'
import type { IntlShape } from 'react-intl'

import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { getOfferDeparturesSortedByDate } from 'lib/cruises/cruiseOfferUtils'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { getCruiseDepartureSelectionInItinerary } from 'tripPlanner/utils/chatItinerary'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'

export type CruiseDeparture = App.CruiseDepartureSummary | App.CruiseDepartureDetails
type CruiseDateValue = string | null | undefined

export type CruiseItineraryAction =
  | { type: 'add' }
  | { type: 'update'; itemId?: number }
  | { type: 'delete'; itemId: number }

interface BuildCruiseItineraryActionMessageParams {
  cruise: App.CruiseOffer
  departure: CruiseDeparture
  action: CruiseItineraryAction
  intl: IntlShape
  occupancies?: Array<string>
}

function getDepartureSortValue(departure: CruiseDeparture): number {
  return moment(departure.departureDate).unix()
}

function hasDepartureDates(departure: CruiseDeparture): boolean {
  return !!departure.departureDate && !!departure.arrivalDate
}

function formatDate(date: CruiseDateValue): string | undefined {
  if (!date) return undefined
  return moment(date).format(DMY_CASUAL_FORMAT)
}

export function getCruiseDepartures(cruise?: App.CruiseOffer): Array<CruiseDeparture> {
  if (!cruise) return []

  if ('departuresSummary' in cruise && cruise.departuresSummary) {
    return getOfferDeparturesSortedByDate(cruise.departuresSummary)
      .filter(hasDepartureDates)
  }

  return Object.values(cruise.departures ?? {})
    .flat()
    .filter(hasDepartureDates)
    .sort((a, b) => getDepartureSortValue(a) - getDepartureSortValue(b))
}

export function getTripDatesSummary(intl: IntlShape, itineraryData?: TripItineraryData): string | undefined {
  const stops = itineraryData?.stops ?? []
  const datedStops = stops
    .map(stop => ({
      name: stop.placeName,
      dates: stop.days
        .map(day => day.date)
        .filter((date): date is string => !!date),
    }))
    .filter(stop => stop.dates.length > 0)

  if (datedStops.length === 0) return undefined

  const firstStop = datedStops[0]
  const lastStop = datedStops[datedStops.length - 1]
  const startDate = firstStop.dates[0]
  const endDate = lastStop.dates[lastStop.dates.length - 1]

  if (!startDate || !endDate) return undefined

  const nights = moment(endDate).diff(moment(startDate), 'days')
  const startDateLabel = formatDate(startDate)
  const endDateLabel = formatDate(endDate)

  if (firstStop.name === lastStop.name) {
    return intl.formatMessage(
      {
        id: 'QvwMnRaHW5TOxVdi7AR9',
        defaultMessage: '{placeName} · {startDateLabel} - {endDateLabel} · {nights, plural, one {# night} other {# nights}}',
        description: 'Trip date summary for one destination shown in the cruise departure selection modal',
      },
      {
        placeName: firstStop.name,
        startDateLabel,
        endDateLabel,
        nights,
      },
    )
  }

  return intl.formatMessage(
    {
      id: 'MeCHCEHyh-vbHQ4Xnx4c',
      defaultMessage: '{startPlaceName} to {endPlaceName} · {startDateLabel} - {endDateLabel} · {nights, plural, one {# night} other {# nights}}',
      description: 'Trip date summary for multiple destinations shown in the cruise departure selection modal',
    },
    {
      startPlaceName: firstStop.name,
      endPlaceName: lastStop.name,
      startDateLabel,
      endDateLabel,
      nights,
    },
  )
}

export function findDepartureBySelection(
  departures: Array<CruiseDeparture>,
  selection: ReturnType<typeof getCruiseDepartureSelectionInItinerary>,
): CruiseDeparture | undefined {
  if (!selection) return undefined

  if (selection.departureId) {
    const departureById = departures.find(departure => departure.id === selection.departureId)
    if (departureById) return departureById
    if (!selection.departureDate) return undefined
  }

  return departures.find(departure => !selection.departureDate || departure.departureDate === selection.departureDate)
}

export function getCruiseRouteLabel(intl: IntlShape, cruise?: App.CruiseOffer): string | undefined {
  if (!cruise?.departurePort && !cruise?.returnPort) return undefined
  if (!cruise.returnPort) return cruise.departurePort
  if (!cruise.departurePort) return cruise.returnPort
  if (cruise.departurePort === cruise.returnPort) {
    return intl.formatMessage(
      {
        id: 'tPwsvNbP_On4wz32T7yv',
        defaultMessage: '{departurePort} round trip',
        description: 'Compact route label shown after a round trip cruise is added to the trip itinerary',
      },
      { departurePort: cruise.departurePort },
    )
  }
  return intl.formatMessage(
    {
      id: 'GuJb-M3qACVXz6WhD_d9',
      defaultMessage: '{departurePort} to {returnPort}',
      description: 'Compact route label shown after a cruise between two ports is added to the trip itinerary',
    },
    {
      departurePort: cruise.departurePort,
      returnPort: cruise.returnPort,
    },
  )
}

export function buildCruiseItineraryActionMessage({
  cruise,
  departure,
  action,
  intl,
  occupancies,
}: BuildCruiseItineraryActionMessageParams): string {
  const visibleMessage = (() => {
    if (action.type === 'add') {
      return intl.formatMessage(
        {
          id: 'nUKFedNdpyLWrq-nGw02',
          defaultMessage: 'Add "{cruiseName}" to my itinerary.',
          description: 'Visible chat message sent when adding a selected cruise to the trip itinerary',
        },
        { cruiseName: cruise.name },
      )
    }

    if (action.type === 'update') {
      return intl.formatMessage(
        {
          id: 'GJ_FVJUx3_rc69iSyBzB',
          defaultMessage: 'Update "{cruiseName}" in my itinerary.',
          description: 'Visible chat message sent when updating an existing cruise in the trip itinerary',
        },
        { cruiseName: cruise.name },
      )
    }

    return intl.formatMessage(
      {
        id: 'Nd-zHhfPQRvchgohGW7f',
        defaultMessage: 'Remove "{cruiseName}" from my itinerary.',
        description: 'Visible chat message sent when removing a selected cruise from the trip itinerary',
      },
      { cruiseName: cruise.name },
    )
  })()

  const actionContextLines = (() => {
    if (action.type === 'add') {
      return [
        'Add cruise to itinerary',
        'Agent instruction: Create any required trip stops from the selected cruise ports and place the cruise appropriately in the itinerary. Use update_itinerary add_cruise with the exact selected sailing fields below; do not infer or substitute another sailing.',
      ]
    }

    if (action.type === 'update') {
      return [
        'Update cruise in itinerary',
        action.itemId !== undefined ? `itemId: ${action.itemId}` : undefined,
        action.itemId !== undefined ?
          'Agent instruction: Replace only this cruise itinerary item. Use update_itinerary delete_item with its itemId, do not delete any stops, then use update_itinerary add_cruise with the exact selected sailing fields below. Create any required stops before adding the cruise.' :
          'Agent instruction: Find the current cruise itinerary item for this offer, remove only that item with update_itinerary delete_item without deleting stops, then use update_itinerary add_cruise with the exact selected sailing fields below. Create any required stops before adding the cruise.',
      ]
    }

    return [
      'Remove cruise from itinerary',
      `itemId: ${action.itemId}`,
      'Agent instruction: Use update_itinerary delete_item to remove exactly this cruise itinerary item. Then use discretion based on the itinerary and chat history: remove stops that are no longer useful without this cruise, but preserve stops that remain useful to the trip.',
    ]
  })()

  const contextLines = [
    ...actionContextLines,
    `leOfferId: ${cruise.id}`,
    `Cruise name: ${cruise.name}`,
    `departureId: ${departure.id}`,
    departure.departureDate ? `departureDate: ${departure.departureDate}` : undefined,
    departure.arrivalDate ? `arrivalDate: ${departure.arrivalDate}` : undefined,
    occupancies?.length ? `Selected cabin occupancies (one per cabin): ${occupancies.join('; ')}` : undefined,
    cruise.departurePort ? `Departure port: ${cruise.departurePort}` : undefined,
    cruise.returnPort ? `Return port: ${cruise.returnPort}` : undefined,
    cruise.ship?.name ? `Ship: ${cruise.ship.name}` : undefined,
    cruise.cruiseLine?.name ? `Cruise line: ${cruise.cruiseLine.name}` : undefined,
  ].filter((line): line is string => !!line)

  return appendContextTag(
    visibleMessage,
    contextLines.join('\n'),
    TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  )
}
